import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import CircularProgress from '@mui/material/CircularProgress'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import Database from '../../../types/Database'
import { useTestDataStore } from '../../../stores/testDataStore'

type DiveStatus = Database.Dive['status']

interface DiveRow {
  id: string
  hostId: string
  siteName: string
  description: string
  locationDisplay: string
  dateTimeDisplay: string
  status: Database.Dive['status']
  price: number | null
  maxParticipants: number
  moderationReason: string | null
  previousStatus: string | null
}

const STATUS_COLORS: Record<
  DiveStatus,
  'success' | 'info' | 'warning' | 'error' | 'default'
> = {
  open: 'success',
  full: 'info',
  cancelled: 'warning',
  completed: 'default',
  removed: 'error'
}

const DiveModerationTab = () => {
  const showTestData = useTestDataStore(s => s.showTestData)
  const [dives, setDives] = useState<DiveRow[]>([])
  const [filteredDives, setFilteredDives] = useState<DiveRow[]>([])
  const [loading, setLoading] = useState(true)
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState<DiveStatus | 'all'>('all')
  const [selectedDive, setSelectedDive] = useState<DiveRow | null>(null)
  const [hostNames, setHostNames] = useState<Record<string, string>>({})

  // Remove dialog state
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [removeReason, setRemoveReason] = useState('')
  const [removeLoading, setRemoveLoading] = useState(false)

  // Restore state
  const [restoreLoading, setRestoreLoading] = useState(false)

  // Feedback state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const fetchDives = useCallback(async () => {
    setLoading(true)
    try {
      const db = getFirestore()
      const divesRef = collection(db, 'dives')
      const q = query(divesRef, where('testData', '==', showTestData))
      const snapshot = await getDocs(q)
      const diveList: DiveRow[] = snapshot.docs.map(d => {
        const data = d.data()
        const location = data.location
        const dateTime = data.dateTime
        return {
          id: d.id,
          hostId: data.hostId as string,
          siteName: data.siteName as string,
          description: data.description as string,
          locationDisplay: location
            ? `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(
                4
              )}`
            : '—',
          dateTimeDisplay: dateTime?.toDate?.()
            ? dateTime.toDate().toLocaleString()
            : '—',
          status: data.status as Database.Dive['status'],
          price: (data.price as number | null) ?? null,
          maxParticipants: data.maxParticipants as number,
          moderationReason: (data.moderationReason as string | null) ?? null,
          previousStatus: (data.previousStatus as string | null) ?? null
        }
      })
      setDives(diveList)

      // Fetch unique host names
      const uniqueHostIds = [...new Set(diveList.map(d => d.hostId))]
      const names: Record<string, string> = {}
      await Promise.all(
        uniqueHostIds.map(async hostId => {
          try {
            const userDoc = await getDoc(doc(db, 'users', hostId))
            if (userDoc.exists()) {
              names[hostId] = (userDoc.data() as Database.User).displayName
            } else {
              names[hostId] = hostId
            }
          } catch {
            names[hostId] = hostId
          }
        })
      )
      setHostNames(names)
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to load dives: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error'
      })
    } finally {
      setLoading(false)
    }
  }, [showTestData])

  useEffect(() => {
    fetchDives()
  }, [fetchDives, showTestData])

  // Client-side filtering
  useEffect(() => {
    let result = dives

    if (statusFilter !== 'all') {
      result = result.filter(d => d.status === statusFilter)
    }

    if (searchText.trim()) {
      const lower = searchText.toLowerCase()
      result = result.filter(d => {
        const hostName = hostNames[d.hostId] ?? ''
        return (
          d.siteName.toLowerCase().includes(lower) ||
          hostName.toLowerCase().includes(lower)
        )
      })
    }

    setFilteredDives(result)
  }, [dives, searchText, statusFilter, hostNames])

  const handleRemoveListing = async () => {
    if (!selectedDive) return
    if (removeReason.length < 1 || removeReason.length > 500) return

    setRemoveLoading(true)
    try {
      const functions = getFunctions()
      const removeDiveListing = httpsCallable<
        { diveId: string; reason: string },
        { success: boolean }
      >(functions, 'removeDiveListing')

      await removeDiveListing({ diveId: selectedDive.id, reason: removeReason })

      setSnackbar({
        open: true,
        message: 'Dive listing removed successfully.',
        severity: 'success'
      })
      setRemoveDialogOpen(false)
      setRemoveReason('')
      setSelectedDive(null)
      await fetchDives()
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to remove listing: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error'
      })
    } finally {
      setRemoveLoading(false)
    }
  }

  const handleRestoreListing = async () => {
    if (!selectedDive) return

    setRestoreLoading(true)
    try {
      const functions = getFunctions()
      const restoreDiveListing = httpsCallable<
        { diveId: string },
        { success: boolean }
      >(functions, 'restoreDiveListing')

      await restoreDiveListing({ diveId: selectedDive.id })

      setSnackbar({
        open: true,
        message: 'Dive listing restored successfully.',
        severity: 'success'
      })
      setSelectedDive(null)
      await fetchDives()
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to restore listing: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error'
      })
    } finally {
      setRestoreLoading(false)
    }
  }

  const reasonError =
    removeReason.length > 0 &&
    (removeReason.length < 1 || removeReason.length > 500)
      ? 'Reason must be 1–500 characters'
      : ''

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' py={6}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant='h6' sx={{ mb: 2 }}>
        Dive Moderation
      </Typography>

      {/* Search and Filter Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label='Search by title or host'
          variant='outlined'
          size='small'
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          sx={{ minWidth: 250 }}
        />
        <FormControl size='small' sx={{ minWidth: 150 }}>
          <InputLabel id='status-filter-label'>Status</InputLabel>
          <Select
            labelId='status-filter-label'
            value={statusFilter}
            label='Status'
            onChange={e =>
              setStatusFilter(e.target.value as DiveStatus | 'all')
            }
          >
            <MenuItem value='all'>All</MenuItem>
            <MenuItem value='open'>Open</MenuItem>
            <MenuItem value='full'>Full</MenuItem>
            <MenuItem value='cancelled'>Cancelled</MenuItem>
            <MenuItem value='completed'>Completed</MenuItem>
            <MenuItem value='removed'>Removed</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Dives Table */}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Host</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDives.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align='center'>
                  <Typography color='text.secondary'>
                    No dives found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredDives.map(dive => (
                <TableRow
                  key={dive.id}
                  hover
                  selected={selectedDive?.id === dive.id}
                  onClick={() => setSelectedDive(dive)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{dive.siteName}</TableCell>
                  <TableCell>{hostNames[dive.hostId] ?? dive.hostId}</TableCell>
                  <TableCell>{dive.dateTimeDisplay}</TableCell>
                  <TableCell>
                    <Chip
                      label={dive.status}
                      color={STATUS_COLORS[dive.status] ?? 'default'}
                      size='small'
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size='small'
                      variant='text'
                      onClick={e => {
                        e.stopPropagation()
                        setSelectedDive(dive)
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Panel */}
      {selectedDive && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mb: 1 }}>
            Listing Details
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Typography variant='body2'>
              <strong>Title:</strong> {selectedDive.siteName}
            </Typography>
            <Typography variant='body2'>
              <strong>Host:</strong>{' '}
              {hostNames[selectedDive.hostId] ?? selectedDive.hostId}
            </Typography>
            <Typography variant='body2'>
              <strong>Description:</strong> {selectedDive.description}
            </Typography>
            <Typography variant='body2'>
              <strong>Location:</strong> {selectedDive.locationDisplay}
            </Typography>
            <Typography variant='body2'>
              <strong>Date:</strong> {selectedDive.dateTimeDisplay}
            </Typography>
            <Typography variant='body2'>
              <strong>Price:</strong>{' '}
              {selectedDive.price != null ? `$${selectedDive.price}` : 'Free'}
            </Typography>
            <Typography variant='body2'>
              <strong>Capacity:</strong> {selectedDive.maxParticipants}
            </Typography>
            <Typography variant='body2'>
              <strong>Status:</strong>{' '}
              <Chip
                label={selectedDive.status}
                color={STATUS_COLORS[selectedDive.status] ?? 'default'}
                size='small'
              />
            </Typography>
          </Box>

          {selectedDive.status === 'removed' &&
            selectedDive.moderationReason && (
              <Alert severity='warning' sx={{ mt: 2 }}>
                <strong>Removal reason:</strong> {selectedDive.moderationReason}
              </Alert>
            )}

          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            {selectedDive.status !== 'removed' && (
              <Button
                variant='contained'
                color='error'
                onClick={() => setRemoveDialogOpen(true)}
              >
                Remove Listing
              </Button>
            )}
            {selectedDive.status === 'removed' && (
              <Button
                variant='contained'
                color='primary'
                onClick={handleRestoreListing}
                disabled={restoreLoading}
              >
                {restoreLoading ? 'Restoring...' : 'Restore Listing'}
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {/* Remove Dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={() => {
          if (!removeLoading) {
            setRemoveDialogOpen(false)
            setRemoveReason('')
          }
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Remove Dive Listing</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2 }}>
            Please provide a reason for removing &quot;{selectedDive?.siteName}
            &quot;.
          </Typography>
          <TextField
            label='Reason'
            multiline
            rows={3}
            fullWidth
            value={removeReason}
            onChange={e => setRemoveReason(e.target.value)}
            error={!!reasonError}
            helperText={reasonError || `${removeReason.length}/500`}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRemoveDialogOpen(false)
              setRemoveReason('')
            }}
            disabled={removeLoading}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleRemoveListing}
            disabled={
              removeLoading ||
              removeReason.length < 1 ||
              removeReason.length > 500
            }
          >
            {removeLoading ? 'Removing...' : 'Confirm Removal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant='filled'
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default DiveModerationTab
